import type { AssetQaCheckDto, AssetQaFindingDto } from '@/types/assetQa'
import { findingKey } from './assetQaFindingKey'

/**
 * Rows that are one row said many times, folded for reading.
 *
 * A gradient is sampled as dozens of separate flat regions, so the brand-colour check reports
 * dozens of times about a single palette role. The server marks those rows with a shared
 * `GroupKey`; this turns them into one entry carrying its members.
 *
 * ⚑ Folding is presentation only. Every member is still here, an override is still recorded
 * against each one, and expanding a group shows the raw shades — the fold is what a reader sees
 * first, never what is kept.
 */
export interface AssetQaGroup<T> {
    /** Stable within a rendered run. The group key where there is one, the row's own key otherwise. */
    key: string
    /** The row shown when the group is collapsed. The first member, which is also the largest region. */
    lead: T
    members: T[]
    /** The palette role the group is about, or null when the rows name none. */
    role: string | null
    /**
     * Whether these rows are gradients rather than flat colours (ORB-980).
     *
     * ⚑ Gradients fold together as gradients rather than by palette role, so this group's `role` is
     * deliberately null — two gradients on one asset usually sit nearest different roles, and what a
     * reader wants collapsed is "this asset has gradients in it".
     */
    isGradient: boolean
    /** The sampled colours, in the order they were measured. Empty for rows that are not colours. */
    hexes: string[]
    /** Lowest and highest measured distance across the members, or null where none carry one. */
    range: { low: number; high: number } | null
}

const COLOUR_CHECK_PREFIX = 'brand-colour:'

/**
 * The role is the last segment of the key.
 *
 * ⚑ By construction, not by luck: `AssetQaColourGrouping.KeyFor` builds the key as
 * `brand-colour|status|code|role` and writes the literal "unnamed" when it could not read a role
 * off the sentence. The two have to change together.
 */
function roleFromKey(groupKey: string): string | null {
    const segments = groupKey.split('|')
    const role = segments[segments.length - 1]
    return !role || role === 'unnamed' || role === GRADIENT_ROLE ? null : role
}

/**
 * The literal the server writes in the role slot for a gradient.
 *
 * ⚑ Must match `AssetQaColourGrouping.GradientRole`. The two change together, the same way the
 * "unnamed" literal above already does.
 */
const GRADIENT_ROLE = 'gradient'

function isGradientKey(groupKey: string): boolean {
    return groupKey.split('|').pop() === GRADIENT_ROLE
}

/** The sampled colour, carried structurally in the check id rather than in the prose. */
function hexFromCheckId(checkId: string | null | undefined): string | null {
    return checkId?.startsWith(COLOUR_CHECK_PREFIX)
        ? checkId.slice(COLOUR_CHECK_PREFIX.length)
        : null
}

/** The figure out of "ΔE 10.0 · 1.6° from its hue". */
function deltaOf(measured: string | null | undefined): number | null {
    const match = measured?.match(/ΔE\s*(\d+(?:\.\d+)?)/)
    if (!match) return null

    const value = Number(match[1])
    return Number.isFinite(value) ? value : null
}

function rangeOf(deltas: number[]): { low: number; high: number } | null {
    return deltas.length === 0
        ? null
        : { low: Math.min(...deltas), high: Math.max(...deltas) }
}

/**
 * Folds rows onto their group key, preserving the order each group first appeared in.
 *
 * A row with no group key becomes a group of one, so a caller renders one shape rather than
 * branching on whether folding applied.
 */
function fold<T>(
    rows: T[],
    groupKeyOf: (row: T) => string | null | undefined,
    soloKeyOf: (row: T, index: number) => string,
    checkIdOf: (row: T) => string | null | undefined,
    measuredOf: (row: T) => string | null | undefined
): AssetQaGroup<T>[] {
    const byKey = new Map<string, AssetQaGroup<T>>()

    rows.forEach((row, index) => {
        const grouped = groupKeyOf(row)
        const key = grouped || soloKeyOf(row, index)
        const existing = byKey.get(key)

        if (existing) {
            existing.members.push(row)
            return
        }

        byKey.set(key, {
            key,
            lead: row,
            members: [row],
            role: grouped ? roleFromKey(grouped) : null,
            isGradient: grouped ? isGradientKey(grouped) : false,
            hexes: [],
            range: null,
        })
    })

    // Derived once the members are all in, rather than maintained as each one arrives.
    return [...byKey.values()].map(group => ({
        ...group,
        hexes: group.members
            .map(member => hexFromCheckId(checkIdOf(member)))
            .filter((hex): hex is string => hex !== null),
        range: rangeOf(
            group.members
                .map(member => deltaOf(measuredOf(member)))
                .filter((delta): delta is number => delta !== null)
        ),
    }))
}

export function groupFindings(findings: AssetQaFindingDto[]): AssetQaGroup<AssetQaFindingDto>[] {
    return fold(
        findings,
        finding => finding.GroupKey,
        (finding, index) => findingKey(finding, index),
        finding => finding.CheckId,
        finding => finding.Measured
    )
}

export function groupChecks(checks: AssetQaCheckDto[]): AssetQaGroup<AssetQaCheckDto>[] {
    return fold(
        checks,
        check => check.GroupKey,
        check => check.Id,
        check => check.Id,
        check => check.Measured
    )
}

/**
 * How a folded group describes itself: how many shades, of which role, over what spread.
 *
 * Reads as "43 shades near Secondary, ΔE 10.0–21.6". The count is first because it is the thing a
 * reader is missing when the same sentence is printed 43 times.
 */
export function describeGroup(group: AssetQaGroup<unknown>): string | null {
    if (group.members.length < 2) return null

    const role = group.role ? ` near ${group.role}` : ''
    const spread = group.range
        ? group.range.low === group.range.high
            ? `, ΔE ${group.range.low.toFixed(1)}`
            : `, ΔE ${group.range.low.toFixed(1)}–${group.range.high.toFixed(1)}`
        : ''

    // ⚑ "gradients", not "shades near X" (ORB-980). A gradient group's members are whole gradients,
    // each already standing for tens of shades, and they are folded on being gradients rather than on
    // a shared palette role — so there is no role to name and a shade count would be the wrong unit.
    if (group.isGradient) return `${group.members.length} gradients`

    return `${group.members.length} shades${role}${spread}`
}

/** A few of the colours, for a reader who wants to recognise them without expanding. */
export function sampleHexes(group: AssetQaGroup<unknown>, take = 3): string[] {
    return group.hexes.slice(0, take)
}
